import * as React from "react";

import { t } from "@impero-com/common/utils/i18n";
import { Divider, InfoTooltip } from "@impero-com/ui";
import memoizeOne from "memoize-one";

import {
    SubsequentStepIndex,
    Month,
    SchedulingOccurrenceChangeForDueDateOffsetForMonthOffsetPeriod as SchedulingOccurrenceChange,
    UpdatableRecurrenceRuleWithOffsetForMonthAndDueDateOffsetForMonthOffsetPeriodAndUpdatableDueDateOffsetForUpdatableMonthOffsetPeriod as UpdatableMonthlyRecurrenceRule,
} from "api/generated/model";
import { BaseFormDisplay } from "components/form/legacyFields/formDisplay";
import { isNotUndefined } from "utils/typePredicates";

import type { PeriodType } from "../common/multiDefaultDueDateOffsetInput";
import {
    GenericOccurrencesArray,
    MonthlyRecurrenceRuleWithArrays,
} from "../common/rulesWithArrays";

import { MultiMonthCheckboxes } from "./multiMonthCheckboxes";

const toMonthsArrayRecords = memoizeOne(
    (occurrences: GenericOccurrencesArray<Month>): Record<Month, boolean[]> => {
        return {
            January: occurrences.January.map(isNotUndefined),
            February: occurrences.February.map(isNotUndefined),
            March: occurrences.March.map(isNotUndefined),
            April: occurrences.April.map(isNotUndefined),
            May: occurrences.May.map(isNotUndefined),
            June: occurrences.June.map(isNotUndefined),
            July: occurrences.July.map(isNotUndefined),
            August: occurrences.August.map(isNotUndefined),
            September: occurrences.September.map(isNotUndefined),
            October: occurrences.October.map(isNotUndefined),
            November: occurrences.November.map(isNotUndefined),
            December: occurrences.December.map(isNotUndefined),
        };
    }
);

abstract class BaseMonthlyRecurrenceRuleUpdaterForm<
    P,
> extends React.PureComponent<P> {
    render = () => {
        return (
            <>
                <Divider />
                {this.buildDefaultDueDateOffsetInput()}
                <Divider />
                <h1>
                    {t("CreateControl_Planning_Title")}&nbsp;
                    <InfoTooltip
                        text={t("Control_ModifySchedule_PlanningInfo")}
                    />
                </h1>
                {this.buildResponsibleAllocatedTimeInputs()}
                {this.buildReviewerAllocatedTimeInputs("SubsequentStep0")}
                {this.buildReviewerAllocatedTimeInputs("SubsequentStep1")}
                <Divider />
                {this.buildMonths()}
                {this.buildCustomDueDateDialog()}
                {this.buildRemindersWidget()}
            </>
        );
    };

    abstract buildDefaultDueDateOffsetInput(): React.ReactNode;

    abstract buildResponsibleAllocatedTimeInputs(): React.ReactNode;

    abstract buildReviewerAllocatedTimeInputs(
        subsequentStepIndex: SubsequentStepIndex
    ): React.ReactNode;

    abstract buildMonths(): React.ReactNode;

    abstract buildCustomDueDateDialog(): React.ReactNode;

    abstract buildRemindersWidget(): React.ReactNode;
}

interface BasePartialMonthlyRecurrenceRuleProps {
    currentRule: MonthlyRecurrenceRuleWithArrays;
    buildDefaultDueDateOffsetInput: (
        readOnly: "EDIT" | "READ_ONLY",
        period: PeriodType
    ) => React.ReactNode;
    buildResponsibleAllocatedTimeInputs: (
        readOnly: "EDIT" | "READ_ONLY"
    ) => React.ReactNode;
    buildReviewerAllocatedTimeInputs: (
        readOnly: "EDIT" | "READ_ONLY",
        subsequentStepIndex: SubsequentStepIndex
    ) => React.ReactNode;
    buildCustomDueDateDialog: (
        readOnly: "EDIT" | "READ_ONLY"
    ) => React.ReactNode;
    buildRemindersWidget: (readOnly: "EDIT" | "READ_ONLY") => React.ReactNode;
}

type PartialMonthlyRecurrenceRuleUpdaterProps =
    BasePartialMonthlyRecurrenceRuleProps &
        UpdatableMonthlyRecurrenceRule & {
            onChangeMonthOccurrence: (
                interval: Month,
                value: SchedulingOccurrenceChange | undefined
            ) => void;
        };

class PartialMonthlyRecurrenceRuleUpdater extends BaseMonthlyRecurrenceRuleUpdaterForm<PartialMonthlyRecurrenceRuleUpdaterProps> {
    buildDefaultDueDateOffsetInput = () =>
        this.props.buildDefaultDueDateOffsetInput("EDIT", "Month");

    buildResponsibleAllocatedTimeInputs = () =>
        this.props.buildResponsibleAllocatedTimeInputs("EDIT");

    buildReviewerAllocatedTimeInputs = (
        subsequentStepIndex: SubsequentStepIndex
    ) =>
        this.props.buildReviewerAllocatedTimeInputs(
            "EDIT",
            subsequentStepIndex
        );

    buildMonths = () => {
        const {
            currentRule: { occurrences },
        } = this.props;
        const values = toMonthsArrayRecords(occurrences);
        return (
            <BaseFormDisplay label={t("Control_Scheduling_Advanced")}>
                <MultiMonthCheckboxes
                    type="EDIT"
                    months={values}
                    onChange={this.onChangeMonth}
                />
            </BaseFormDisplay>
        );
    };

    onChangeMonth = (key: Month, value: boolean) => {
        const { onChangeMonthOccurrence } = this.props;
        onChangeMonthOccurrence(
            key,
            value ? "ActivateOnlyDeactivated" : undefined
        );
    };

    buildCustomDueDateDialog = () =>
        this.props.buildCustomDueDateDialog("EDIT");

    buildRemindersWidget = () => this.props.buildRemindersWidget("EDIT");
}

interface FullMonthlyRecurrenceRuleUpdaterProps {
    currentRule: MonthlyRecurrenceRuleWithArrays;
    buildDefaultDueDateOffsetInput: (
        readOnly: "EDIT",
        period: PeriodType
    ) => React.ReactNode;
    buildResponsibleAllocatedTimeInputs: (readOnly: "EDIT") => React.ReactNode;
    buildReviewerAllocatedTimeInputs: (
        readOnly: "EDIT",
        subsequentStepIndex: SubsequentStepIndex
    ) => React.ReactNode;
    buildCustomDueDateDialog: (readOnly: "EDIT") => React.ReactNode;
    buildRemindersWidget: (readOnly: "EDIT") => React.ReactNode;
    onChangeMonthOccurrence: (
        interval: Month,
        value: SchedulingOccurrenceChange | undefined
    ) => void;
}

class FullMonthlyRecurrenceRuleUpdater extends BaseMonthlyRecurrenceRuleUpdaterForm<FullMonthlyRecurrenceRuleUpdaterProps> {
    buildDefaultDueDateOffsetInput = () =>
        this.props.buildDefaultDueDateOffsetInput("EDIT", "Month");

    buildResponsibleAllocatedTimeInputs = () =>
        this.props.buildResponsibleAllocatedTimeInputs("EDIT");

    buildReviewerAllocatedTimeInputs = (
        subsequentStepIndex: SubsequentStepIndex
    ) =>
        this.props.buildReviewerAllocatedTimeInputs(
            "EDIT",
            subsequentStepIndex
        );

    buildMonths = () => {
        const {
            currentRule: { occurrences },
        } = this.props;
        const values = toMonthsArrayRecords(occurrences);
        return (
            <BaseFormDisplay label={t("Control_Scheduling_Advanced")}>
                <MultiMonthCheckboxes
                    type="EDIT"
                    months={values}
                    onChange={this.onChangeMonth}
                />
            </BaseFormDisplay>
        );
    };

    onChangeMonth = (key: Month, value: boolean) => {
        const { onChangeMonthOccurrence } = this.props;
        onChangeMonthOccurrence(
            key,
            value ? "ActivateOnlyDeactivated" : undefined
        );
    };

    buildCustomDueDateDialog = () =>
        this.props.buildCustomDueDateDialog("EDIT");

    buildRemindersWidget = () => this.props.buildRemindersWidget("EDIT");
}

class PartialMonthlyRecurrenceRuleViewer extends BaseMonthlyRecurrenceRuleUpdaterForm<PartialMonthlyRecurrenceRuleViewerProps> {
    buildDefaultDueDateOffsetInput = () =>
        this.props.buildDefaultDueDateOffsetInput("READ_ONLY", "Month");

    buildResponsibleAllocatedTimeInputs = () =>
        this.props.buildResponsibleAllocatedTimeInputs("READ_ONLY");

    buildReviewerAllocatedTimeInputs = (
        subsequentStepIndex: SubsequentStepIndex
    ) =>
        this.props.buildReviewerAllocatedTimeInputs(
            "READ_ONLY",
            subsequentStepIndex
        );

    buildMonths = () => {
        const {
            currentRule: { occurrences },
        } = this.props;
        const values = toMonthsArrayRecords(occurrences);
        return (
            <BaseFormDisplay label={t("Control_Scheduling_Advanced")}>
                <MultiMonthCheckboxes type="READ_ONLY" months={values} />
            </BaseFormDisplay>
        );
    };

    buildCustomDueDateDialog = () =>
        this.props.buildCustomDueDateDialog("READ_ONLY");

    buildRemindersWidget = () => this.props.buildRemindersWidget("READ_ONLY");
}

type PartialMonthlyRecurrenceRuleViewerProps =
    BasePartialMonthlyRecurrenceRuleProps;

export {
    FullMonthlyRecurrenceRuleUpdater,
    PartialMonthlyRecurrenceRuleUpdater,
    PartialMonthlyRecurrenceRuleViewer,
};
